import React, { useState, Fragment, useEffect, memo } from "react";
import { Users, Eye, Search, ChevronDown, Loader } from 'lucide-react';

// import custom component
import Pagination from "./Pagination";

const DataTable = ({ columns, data, count, loader, setPagination }) => {

    const limitOptions = [5, 10, 20, 50, 100];
    // state
    const [pageDoc, setPageDoc] = useState({ page: 1, limit: 10, search: "" });
    const [showLimitDropdown, setShowLimitDropdown] = useState(false);

    const { page, limit, search } = pageDoc;

    const handleLimitChange = (limit) => {
        setPagination(prevState => ({ ...prevState, ...{ limit, page: 1 } })); // Reset to page 1 when limit changes
        setPageDoc({ ...pageDoc, ...{ limit, page: 1 } }); // Reset to page 1 when limit changes
        setShowLimitDropdown(false);
    };

    const handleSearch = (e) => {
        setPagination(prevState => ({ ...prevState, ...{ search: e.target.value, page: 1 } }));
        setPageDoc({ ...pageDoc, ...{ search: e.target.value, page: 1 } })
    };

    return (
        <Fragment>
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Filter in records..."
                            value={search}
                            onChange={handleSearch}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        />
                    </div>
                    <div className="relative">
                        <button
                            onClick={() => setShowLimitDropdown(!showLimitDropdown)}
                            className="flex items-center gap-2 px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-transparent min-w-[120px]"
                        >
                            <span>{limit}</span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showLimitDropdown ? 'rotate-180' : ''}`} />
                        </button>

                        {showLimitDropdown && (
                            <>
                                <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setShowLimitDropdown(false)}
                                />
                                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                                    {limitOptions.map((option) => (
                                        <button
                                            key={option}
                                            onClick={() => handleLimitChange(option)}
                                            className={`w-full px-4 py-2 text-left hover:bg-cyan-50 transition-colors ${limit === option
                                                ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                                                : 'text-gray-700'
                                                } first:rounded-t-lg last:rounded-b-lg`}
                                        >
                                            {option} per page
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                {loader ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader className="w-8 h-8 text-cyan-500 animate-spin" />
                        <span className="ml-3 text-gray-600">Loading...</span>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold">#</th>
                                        {
                                            columns && columns.length > 0 && columns?.map((item, key) => {
                                                return (
                                                    <th className="px-6 py-4 text-left text-sm font-semibold">{item?.text}</th>
                                                )
                                            })
                                        }
                                        {/* <th className="px-6 py-4 text-left text-sm font-semibold">Name</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold">Phone</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold">Password</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold">User ID</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold">PIN Number</th>
                                        <th className="px-6 py-4 text-center text-sm font-semibold">Action</th> */}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {(data && data.length > 0) ? (
                                        data.map((item, index) => (
                                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                    {((page - 1) * limit) + index + 1}
                                                </td>
                                                {
                                                    columns && columns.length > 0 && columns?.map((column, key) => {
                                                        if (column?.cell) {
                                                            return (
                                                                <td key={key} className="px-6 py-4 text-sm text-gray-600">{column?.cell(item)}</td>
                                                            )
                                                        } else {
                                                            return (
                                                                <td key={key} className="px-6 py-4 text-sm text-gray-600">{item[column?.key]}</td>
                                                            )
                                                        }
                                                    })
                                                }
                                                {/* <td className="px-6 py-4 text-sm text-gray-600">{item?.userName}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{item?.phone}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{item?.originalPassword}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                                                        {item?.userCode}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                                        {item?.pinNumber || '-'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <button
                                                        onClick={() => handleViewUser(item)}
                                                        className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        View
                                                    </button>
                                                </td> */}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                                                No records found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            count={count}
                            limit={limit}
                            page={page}
                            setPagination={setPagination}
                            setPageDoc={setPageDoc}
                        />
                    </>
                )}
            </div>
        </Fragment>
    )
}

export default memo(DataTable);